import React, { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast, Toaster } from "sonner";
import DashboardShell from "../../components/layout/DashboardShell";
import { Button, Card, Pill } from "../../components/common/Primitives";
import SandboxKycPanel from "../../components/kyc/SandboxKycPanel";
import KycCompanyDetailsForm from "../../components/kyc/KycCompanyDetailsForm";
import api, { formatApiError } from "../../lib/api";
import { formatDateTime } from "../../lib/datetime";
import { ShieldCheck, FileCheck2, Info, Upload, CheckCircle2, Circle, MessageCircle, Send } from "lucide-react";

/** Turns a machine category (e.g. `pan_card`) into a readable label. */
const CATEGORY_LABEL = {
  pan_card: "PAN Card",
  cin_certificate: "Incorporation / CIN Certificate",
  bank_statement: "Bank Statement (last 12 months)",
  founder_id: "Founders' ID / KYC",
  aadhaar: "Aadhaar",
  gstin: "GSTIN Certificate",
  moa: "MoA / AoA",
};
const humanize = (cat) =>
  CATEGORY_LABEL[cat] ||
  cat.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

export default function StartupKYC() {
  const [me, setMe] = useState(null);
  const [reqs, setReqs] = useState(null);
  const [busy, setBusy] = useState(false);
  const [thread, setThread] = useState([]);
  const [searchParams] = useSearchParams();

  // Deep-link support — email reminders like `/startup/kyc?focus=digilocker`
  // scroll the corresponding Sandbox card into view and briefly highlight it.
  useEffect(() => {
    const focus = searchParams.get("focus");
    if (!focus) return;
    const map = {
      digilocker: "sandbox-digilocker-card",
      pan: "sandbox-pan-card",
      cin: "sandbox-cin-card",
      gstin: "sandbox-gstin-card",
      "company-pan": "sandbox-company-pan-card",
    };
    const target = map[focus];
    if (!target) return;
    const t = setTimeout(() => {
      const el = document.querySelector(`[data-testid='${target}']`);
      if (!el) return;
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.classList.add("ring-4", "ring-gold/70", "transition");
      setTimeout(() => el.classList.remove("ring-4", "ring-gold/70", "transition"), 2500);
    }, 900); // wait for panel to hydrate
    return () => clearTimeout(t);
  }, [searchParams]);
  const [reply, setReply] = useState("");
  const [attachments, setAttachments] = useState([]); // [{doc_id, filename, category}]
  const [uploadBusy, setUploadBusy] = useState(false);

  const refresh = useCallback(async () => {
    const [meRes, reqRes, thRes] = await Promise.all([
      api.get("/startup/me"),
      api.get("/startup/kyc/requirements"),
      api.get("/startup/kyc/thread").catch(() => ({ data: { messages: [] } })),
    ]);
    setMe(meRes.data);
    setReqs(reqRes.data);
    setThread(thRes.data?.messages || []);
  }, []);

  useEffect(() => {
    refresh().catch(() => {});
  }, [refresh]);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/startup/kyc/submit");
      toast.success("KYC submitted. Under review by admin.");
      await refresh();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail && detail.error === "kyc_docs_missing") {
        const missing = (detail.missing || []).map(humanize).join(", ");
        toast.error(missing ? `Missing: ${missing}` : "Upload all required documents first.");
      } else {
        toast.error(formatApiError(err));
      }
    } finally {
      setBusy(false);
    }
  };

  const status = me?.startup?.kyc_status || "Not Submitted";
  const tone = status === "Approved" ? "success" : status === "Under Review" ? "warn" : "muted";
  const checklist = reqs?.checklist || [];
  const ready = !!reqs?.ready;
  const uploadedCount = reqs?.uploaded_count ?? 0;

  return (
    <DashboardShell title="KYC Verification" subtitle="STARTUP · COMPLIANCE">
      <Toaster richColors position="top-right" />
      <Card testId="kyc-panel" className="max-w-2xl">
        <div className="flex items-center gap-3 mb-4">
          <ShieldCheck className="text-gold" size={22} />
          <div>
            <div className="font-heading text-ink text-lg">KYC Status</div>
            <div className="mt-1"><Pill tone={tone} data-testid="kyc-status">{status}</Pill></div>
          </div>
        </div>
        <p className="text-ink-soft text-sm leading-relaxed">
          VenturaZ requires KYC verification before your startup profile is shared with institutional
          investors, family offices and lenders. Upload the documents below in the Document Vault;
          our team will run verification.
        </p>

        {/* Checklist */}
        <div className="mt-6" data-testid="kyc-checklist">
          <div className="text-[11px] font-mono uppercase tracking-widest text-ink-muted mb-2">
            Required Documents · {checklist.filter((i) => i.uploaded).length} of {checklist.length} ready
          </div>
          <ul className="space-y-2 text-sm">
            {checklist.length === 0 && (
              <li className="text-ink-faint text-xs">Checklist loading…</li>
            )}
            {checklist.map((item) => (
              <li
                key={item.category}
                className="flex items-center gap-2"
                data-testid={`kyc-check-${item.category}`}
              >
                {item.uploaded ? (
                  <CheckCircle2 size={16} className="text-emerald-600 shrink-0" />
                ) : (
                  <Circle size={16} className="text-ink-faint shrink-0" />
                )}
                <span className={item.uploaded ? "text-ink" : "text-ink-soft"}>
                  {humanize(item.category)}
                </span>
                {!item.uploaded && (
                  <Link
                    to="/startup/documents"
                    className="ml-auto text-[11px] font-heading text-gold-soft hover:underline flex items-center gap-1"
                    data-testid={`kyc-upload-${item.category}`}
                  >
                    <Upload size={11} /> Upload
                  </Link>
                )}
              </li>
            ))}
          </ul>
        </div>

        <div className="mt-6 flex items-center gap-3 flex-wrap">
          <Button
            onClick={submit}
            disabled={!ready || busy || status === "Under Review" || status === "Approved"}
            data-testid="submit-kyc-btn"
          >
            {busy ? "Submitting…" : ready ? "Submit for KYC Review" : "Complete checklist to submit"}
          </Button>
          <Link
            to="/startup/documents"
            className="text-xs font-heading text-ink-soft hover:text-gold-soft flex items-center gap-1"
            data-testid="open-vault-link"
          >
            <FileCheck2 size={12} /> Open Document Vault
          </Link>
          <div className="text-xs text-ink-faint flex items-center gap-1">
            <Info size={12} /> Admin can reconfigure required docs in Settings.
          </div>
        </div>
      </Card>

      {/* Instant identity checks via Sandbox — DigiLocker + PAN */}
      <div className="mt-6 space-y-4" data-testid="startup-sandbox-kyc">
        <div className="text-[11px] font-mono uppercase tracking-widest text-ink-muted mb-2">
          Instant verification · Powered by DigiLocker & Income Tax
        </div>
        <KycCompanyDetailsForm />
        <SandboxKycPanel showCompany />
      </div>
      {(thread.length > 0 || status === "Under Review" || status === "More Information Required") && (
        <Card testId="kyc-thread-card-founder" className="max-w-2xl mt-6">
          <div className="flex items-center gap-2 mb-3">
            <MessageCircle size={16} className="text-gold" />
            <div>
              <div className="font-heading text-ink text-lg">Reviewer Thread</div>
              <div className="text-xs text-ink-muted">Reply here instead of email so the reviewer sees it immediately.</div>
            </div>
          </div>
          <div className="space-y-2 mb-3 max-h-[360px] overflow-y-auto">
            {thread.length === 0 && (
              <div className="text-xs text-ink-muted italic" data-testid="kyc-thread-empty-founder">
                No messages yet. The reviewer will post here after they review your KYC.
              </div>
            )}
            {thread.map((m) => {
              const mine = m.author_role === "founder";
              return (
                <div
                  key={m.message_id}
                  className={`p-2.5 rounded border text-sm ${mine
                    ? "bg-navy/[0.03] border-navy/10 text-ink"
                    : "bg-emerald-50 border-emerald-200 text-emerald-900"}`}
                  data-testid={`kyc-thread-msg-${m.message_id}`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[10px] font-mono uppercase tracking-widest text-ink-muted">
                      {mine ? "You" : "VenturaZ Reviewer"} · {m.kind}
                    </span>
                    <span className="text-[10px] font-mono text-ink-muted">
                      {m.created_at ? formatDateTime(m.created_at) : ""}
                    </span>
                  </div>
                  <div className="whitespace-pre-wrap text-sm">{m.body}</div>
                  {Array.isArray(m.attachments) && m.attachments.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`kyc-thread-msg-attachments-${m.message_id}`}>
                      {m.attachments.map((a) => (
                        <span key={a.doc_id} className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-white border border-navy/10 text-ink inline-flex items-center gap-1">
                          <FileCheck2 size={10} /> {a.filename}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <div className="flex items-end gap-2">
            <textarea
              value={reply}
              onChange={(e) => setReply(e.target.value)}
              placeholder="Reply to the reviewer — they see it in their queue instantly"
              rows={2}
              className="flex-1 text-sm bg-white border border-navy/15 rounded p-2 text-ink focus:border-gold outline-none resize-none"
              data-testid="kyc-thread-reply-input-founder"
            />
            <label
              className={`px-3 py-2 rounded border border-navy/15 text-xs font-heading text-ink hover:border-gold hover:text-[#8A6600] inline-flex items-center gap-1 cursor-pointer ${uploadBusy ? "opacity-50 pointer-events-none" : ""}`}
              data-testid="kyc-thread-attach-label"
            >
              <Upload size={12} /> {uploadBusy ? "Uploading…" : "Attach"}
              <input
                type="file"
                className="hidden"
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  setUploadBusy(true);
                  try {
                    const fd = new FormData();
                    fd.append("file", file);
                    fd.append("category", "founder_reply");
                    const { data } = await api.post("/startup/documents", fd, { headers: { "Content-Type": "multipart/form-data" } });
                    setAttachments((a) => [...a, {
                      doc_id: data.doc_id, filename: data.filename || file.name, category: "founder_reply",
                    }]);
                    toast.success("Attached — press Send to include in your reply.");
                  } catch (err) {
                    toast.error(formatApiError(err));
                  } finally {
                    setUploadBusy(false);
                    e.target.value = "";
                  }
                }}
              />
            </label>
            <button
              onClick={async () => {
                if (!reply.trim() && attachments.length === 0) return;
                try {
                  await api.post("/startup/kyc/reply", {
                    body: reply.trim() || "(attachment only)",
                    attachments: attachments.length ? attachments : undefined,
                  });
                  setReply("");
                  setAttachments([]);
                  toast.success("Reply sent.");
                  await refresh();
                } catch (e) { toast.error(formatApiError(e)); }
              }}
              disabled={!reply.trim() && attachments.length === 0}
              className="px-3 py-2 rounded bg-navy text-white text-xs font-heading hover:bg-[#256E58] disabled:opacity-40 inline-flex items-center gap-1"
              data-testid="kyc-thread-reply-send-founder"
            >
              <Send size={12} /> Send
            </button>
          </div>
          {attachments.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5" data-testid="kyc-thread-attach-pills">
              {attachments.map((a) => (
                <span key={a.doc_id} className="text-[11px] font-mono px-2 py-0.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800 inline-flex items-center gap-1">
                  <FileCheck2 size={11} /> {a.filename}
                  <button
                    onClick={() => setAttachments((x) => x.filter((y) => y.doc_id !== a.doc_id))}
                    className="ml-1 text-emerald-700 hover:text-rose-700"
                    data-testid={`kyc-thread-attach-remove-${a.doc_id}`}
                  >×</button>
                </span>
              ))}
            </div>
          )}
        </Card>
      )}
    </DashboardShell>
  );
}
